import React, { useMemo, useEffect, useState } from "react";
import container from "@src/container";
import { Loader } from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { useApiRequest } from "@lib/hooks";
import "./course-credentials.scss";
import { mapCredentialData, getGroupIds } from "./course.utils";
import { ExportLearnerAwardsModal } from "./export-credentials.modal";
import { AwardsProgressTracker } from "@components/progress-tracker/progress-tracker";

export const CourseCredentials = ({
  courseData,
  courseId,
  setCredentialData,
  user,
  classroomId,
}) => {
  const { courseService } = container;
  const { userId } = user;
  const [showExportAwardsModal, setShowExportAwardsModal] = useState(false);

  const { response: courseCredentials, loading: courseCredentialsLoading, sendRequest: getCourseCredentials } =
    useApiRequest(() => courseService.getCourseCredentials(courseId), false);

  const allGroupIds = useMemo(() => {
    if (!courseCredentials || !courseData) return [];

    const { mainCredential, courseInfo } = mapCredentialData(
      courseCredentials,
      courseData
    );
    return getGroupIds(mainCredential, courseInfo);
  }, [courseCredentials, courseData]);

  const mappedCredentials = useMemo(() => {
    if (allGroupIds.length === 0) return null;

    if (!courseCredentials || !courseData) return null;

    const { mainCredential, courseInfo } = mapCredentialData(
      courseCredentials,
      courseData
    );

    const toCredential = ({ groupId, progressMap, type }) => ({
      course_id: courseId,
      group_id: groupId,
      credential_type: type,
      is_complete:
        (progressMap?.numerator || 0) / (progressMap?.denominator || 1) === 1,
    });

    const credentials = [
      ...courseInfo.map(toCredential),
      ...(mainCredential ? [toCredential(mainCredential)] : []),
    ];

    return { credentials };
  }, [allGroupIds.length, courseCredentials, courseData]);

  const {
    loading: userCredInfoLoading,
    response: userCredInfo,
    sendRequest: getCredentialInfoByUserId,
  } = useApiRequest(
    () => courseService.getCredentialInfoByUserId(userId, mappedCredentials),
    false
  );

  useEffect(() => {
    if (mappedCredentials) {
      getCredentialInfoByUserId();
    }
  }, [mappedCredentials]);

  useEffect(() => {
    if (courseId) {
     getCourseCredentials();
    }
  }, [courseId]);

  const { mainCredential, courseInfo } = useMemo(() => {
    if (!courseCredentials || !courseData) {
      return { mainCredential: null, courseInfo: [] };
    }

    const { mainCredential: rawMain, courseInfo: rawCourseInfo } =
      mapCredentialData(courseCredentials, courseData);

    if (!userCredInfo) {
      return { mainCredential: rawMain, courseInfo: rawCourseInfo };
    }

    return {
      mainCredential: rawMain,
      courseInfo: rawCourseInfo,
    };
  }, [courseCredentials, courseData, userCredInfo]);

  useEffect(() => {
    if (mainCredential || courseInfo?.length > 0) {
      setCredentialData({
        mainCredential,
        courseInfo,
      });
    }
  }, [mainCredential, courseInfo, setCredentialData]);

  const awardsWithMetadata = useMemo(() => {
    if (!userCredInfo?.credentialInfo) return [];

    const credentialMetadataMap = new Map();

    courseInfo?.forEach((cred) => {
      credentialMetadataMap.set(cred.groupId, {
        type: cred.type,
        title: cred.title,
        description: cred.description,
      });
    });

    if (mainCredential) {
      credentialMetadataMap.set(mainCredential.groupId, {
        type: mainCredential.type,
        title: mainCredential.title,
        description: mainCredential.description,
      });
    }

    return userCredInfo.credentialInfo
      .filter((cred) => allGroupIds.includes(cred.groupId))
      .map((userCred) => ({
        ...userCred,
        ...credentialMetadataMap.get(userCred.groupId),
      }))
      .sort((a, b) => {
        const typeOrder = { MAIN: 0, MICRO: 1 };
        const typeA = typeOrder[a.type] ?? 2;
        const typeB = typeOrder[b.type] ?? 2;

        return typeA - typeB;
      });
  }, [userCredInfo, courseInfo, mainCredential, allGroupIds]);

  const isLoading = useMemo(() => {
    return courseCredentialsLoading || userCredInfoLoading;
  }, [courseCredentialsLoading, userCredInfoLoading]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      {(courseInfo?.length || mainCredential) && (
        <AwardsProgressTracker
          mainCredential={mainCredential}
          courseInfo={courseInfo}
          userCredInfo={userCredInfo}
          isStudentView={true}
          mainActions={
            <Button
              onClick={() => setShowExportAwardsModal(true)}
              icon={["fa-regular", "file-export"]}
            >
              Export
            </Button>
          }
        />
      )}
      <ExportLearnerAwardsModal
        onClose={() => setShowExportAwardsModal(false)}
        user={user}
        open={showExportAwardsModal}
        awards={awardsWithMetadata}
        courseId={courseId}
        classroomId={classroomId}
      />
    </>
  );
};
